// Service-specific FAQs, keyed by service slug. Kept separate for maintainability.
// Candidate (Career) FAQs address "why not just do it myself with ChatGPT/YouTube".
// Employer (People & HR / AI & Digital) FAQs explain SCC's scope and what happens
// after submitting a requirement. No pricing, guarantees, stats or invented claims.

export const SERVICE_FAQS = {
  // ---------- People & HR (employer) ----------
  recruitment: [
    {
      q: "What happens after I submit a requirement?",
      a: "We review your role and its context, then follow up to confirm the details. You'll see the scope and approach before any outreach begins — nothing starts until it's clear.",
    },
    {
      q: "How involved is SCC in the hiring process?",
      a: "As much as you need — from sourcing and screening through to interview coordination and offer support. You remain the decision-maker; we do the legwork and present reasoned shortlists.",
    },
    {
      q: "Do you replace our internal recruiter or work alongside them?",
      a: "Either. We can run the search end-to-end or plug into your existing team and process, depending on what's most useful to you.",
    },
  ],
  "candidate-sourcing": [
    {
      q: "What happens after I share a role?",
      a: "We confirm the target profile with you, build a mapped pipeline of relevant people, and hand over candidates who are both qualified and genuinely interested.",
    },
    {
      q: "How is this different from posting a job?",
      a: "Sourcing reaches people who aren't actively applying — the passive talent job posts miss — matched specifically to your requirement.",
    },
    {
      q: "Are candidates screened before you hand them over?",
      a: "Yes. You receive candidates qualified against your criteria and gauged for interest, not a raw list of names.",
    },
  ],
  rpo: [
    {
      q: "What does SCC actually own in an RPO engagement?",
      a: "As much of the recruitment process as you want — workflow design, sourcing, screening and coordination — acting as an extension of your team.",
    },
    {
      q: "What happens after I submit a requirement?",
      a: "We discuss volume, roles and timelines, agree the scope, then set up the process and reporting before hiring starts.",
    },
    {
      q: "Is this a long-term commitment?",
      a: "Engagements are scoped to your needs — a hiring surge, a specific function, or an ongoing pipeline. We agree the boundaries up front.",
    },
  ],
  "hr-consulting": [
    {
      q: "What happens after I describe our HR needs?",
      a: "We start with a short conversation to understand your context, then propose a focused scope — not an oversized program you don't need.",
    },
    {
      q: "Do you deliver documents or ongoing support?",
      a: "Both are possible — from one-off policy and process work to advisory support on day-to-day people issues.",
    },
    {
      q: "Is this suitable for a small team?",
      a: "Yes. We tailor the depth to your size and stage so the foundations fit how you actually operate.",
    },
  ],
  "workforce-planning": [
    {
      q: "What does a workforce planning engagement produce?",
      a: "A clear, phased hiring and skills roadmap tied to your business goals — something you can act on, not a theoretical deck.",
    },
    {
      q: "What happens after I submit our situation?",
      a: "We review your current capability and plans, then work with you to forecast demand and shape the roadmap together.",
    },
    {
      q: "How is this different from hiring when we need to?",
      a: "Planning ahead means the right skills arrive at the right time, which avoids rushed, reactive hires later.",
    },
  ],
  "talent-strategy": [
    {
      q: "What happens after I get in touch?",
      a: "We align on your business objectives first, then map attraction, development and retention into one coherent strategy.",
    },
    {
      q: "What will we walk away with?",
      a: "A measurable talent roadmap that connects people decisions to outcomes — specific to you, not generic best-practice.",
    },
    {
      q: "Do you help implement it too?",
      a: "We can. Strategy pairs naturally with our recruitment, RPO and HR capabilities to put the plan into action.",
    },
  ],

  // ---------- AI & Digital (employer) ----------
  "business-website-development": [
    {
      q: "What happens after I submit my website requirement?",
      a: "We discuss your goals, content and structure, agree the scope, then design and build — keeping you involved at each stage.",
    },
    {
      q: "What's included, and what do I need to provide?",
      a: "We handle structure, design, build and launch; you provide content and brand input, with our guidance where you need it.",
    },
    {
      q: "Will I be able to update it later?",
      a: "Yes — we build with maintainability in mind and set foundations you can extend as your business grows.",
    },
  ],
  "ai-powered-website-development": [
    {
      q: "How is AI used in the build?",
      a: "AI accelerates parts of design and content while our team reviews everything — so you get speed without losing quality or control.",
    },
    {
      q: "Does 'AI-powered' mean lower quality?",
      a: "No. AI speeds up the process; human judgement and quality control stay in the loop from start to launch.",
    },
    {
      q: "What happens after I submit a requirement?",
      a: "We scope your goals, agree the approach, then build with AI-assisted steps and human review before anything goes live.",
    },
  ],

  // ---------- Career (candidate) ----------
  "resume-building": [
    {
      q: "Why not just build my resume with ChatGPT or a template?",
      a: "Tools give you generic output. We bring judgement — deciding what to emphasise, how to position your experience for your target roles, and what recruiters actually respond to.",
    },
    {
      q: "What do you need from me?",
      a: "A picture of your experience and the roles you're targeting. We turn that into a focused, credible resume rather than a keyword dump.",
    },
    {
      q: "How do revisions work?",
      a: "We work with you until the resume genuinely reflects your strengths and reads well for the roles you're pursuing.",
    },
  ],
  "resume-optimisation-ats-review": [
    {
      q: "Can't I just ask ChatGPT if my resume is ATS-friendly?",
      a: "AI can flag surface issues, but it can't judge relevance for a specific role or recruiter. We review structure, keywords and positioning against how real systems and hiring teams read resumes.",
    },
    {
      q: "What will I get back?",
      a: "Clear, prioritised feedback and an optimised resume — not a vague score you can't act on.",
    },
    {
      q: "Will optimisation guarantee interviews?",
      a: "No one can honestly promise that. We make sure your resume isn't the reason you're screened out, and that it represents you at your strongest.",
    },
  ],
  "linkedin-optimisation": [
    {
      q: "Why pay for this when there are free LinkedIn tips on YouTube?",
      a: "General tips don't know your background or goals. We make specific decisions about your headline, story and keywords so the right people and roles find you.",
    },
    {
      q: "What changes will you make?",
      a: "Headline, About section, experience and skills — optimised for both search visibility and how a human reads your profile.",
    },
    {
      q: "Do you manage my profile ongoing?",
      a: "This is focused optimisation: we set you up with a strong profile and guidance to keep it current yourself.",
    },
  ],
  "interview-preparation": [
    {
      q: "Why not just practise with ChatGPT or watch interview videos?",
      a: "Generic practice can't give real, tailored feedback. We run mock interviews for your specific roles and coach how you actually come across.",
    },
    {
      q: "What does a session cover?",
      a: "Company and role preparation, behavioural and technical practice, and honest feedback with frameworks you can reuse.",
    },
    {
      q: "Will this help if I get nervous?",
      a: "That's exactly what we prepare for — structure and rehearsal build genuine confidence, not scripted answers.",
    },
  ],
  "career-consultation": [
    {
      q: "How is this different from googling career advice?",
      a: "Advice online is generic. A consultation is about your situation, your options and the trade-offs specific to you — with a concrete plan at the end.",
    },
    {
      q: "What happens in a consultation?",
      a: "We clarify your goals, work through your realistic options, and leave you with clear, prioritised next steps.",
    },
    {
      q: "Is this a one-off or ongoing?",
      a: "It can be a single focused session or the starting point for further support — that's your choice.",
    },
  ],
  "professional-branding": [
    {
      q: "Can't AI just write my bio and posts?",
      a: "AI can draft words, but branding is about deciding what you stand for and telling one consistent story across everything — that's judgement, not generation.",
    },
    {
      q: "What does this cover?",
      a: "Your positioning and narrative, plus consistent messaging across your resume, LinkedIn and profiles.",
    },
    {
      q: "Is this only for senior professionals?",
      a: "No — anyone raising their profile benefits from a clear, consistent professional identity.",
    },
  ],
  "job-search-strategy": [
    {
      q: "Why do I need a strategy — can't I just apply to lots of jobs?",
      a: "Volume applying burns time and morale. A strategy focuses your effort on the right roles and channels so more of it converts.",
    },
    {
      q: "What will I actually get?",
      a: "A targeted plan: the roles to pursue, where to find them, a tracking system, and a weekly cadence to stay on track.",
    },
    {
      q: "Do you apply to jobs for me?",
      a: "No — we make your search sharper and more effective; you stay in control of your applications.",
    },
  ],
  "cover-letter-application-support": [
    {
      q: "Why not let ChatGPT write my cover letters?",
      a: "AI produces generic letters that sound like everyone else's. We help connect your specific story to what each role needs — which is what actually stands out.",
    },
    {
      q: "Do you write every application for me?",
      a: "We help you craft strong, tailored applications and reusable templates, so quality stays high without starting from scratch each time.",
    },
    {
      q: "What do you need from me?",
      a: "The roles you're targeting and your background; we handle tailoring the message and tone.",
    },
  ],
};
